import { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Participant } from 'livekit-client';
import { Typography } from '@web-chat-core/ui-kit';
import { selectHasNewMessage, selectMessages } from 'state/selectors';
import { useChat } from 'useCase/chat/useChat';
import { MessageType } from 'state/store/chat/type';
import { useParticipantMetadata } from 'useCase/participant/useParticipantMetadata';
import { selectRemoteParticipantById } from 'state/selectors';
import { useStickyBottomScroll } from '../hooks/useStickyBottomScroll';
import { MessagesWrapper, NoMessagesWrapper } from '../view';
import { Message } from './Message';

const getMessageProps = (msg: MessageType, avatars: Map<string, string>) => ({
    avatar: avatars.get(msg.senderId) ?? '',
    showName: msg.showName,
    name: msg.name,
    own: msg.own,
    text: msg.text,
    time: msg.time,
    sid: msg.sid,
    last: msg.last,
});

interface MessagesListProps {
    hasNewSentMessage: boolean;
    onScrollAcknowledged: () => void;
}

export const MessagesList = observer(({ hasNewSentMessage, onScrollAcknowledged }: MessagesListProps) => {
    const { t } = useTranslation();
    const { setIsScrolled } = useChat();
    const { getAvatarFromMetadata } = useParticipantMetadata();

    // контейнер списка сообщений; нужен useStickyBottomScroll для измерений и scrollTo
    const wrapperRef = useRef<HTMLDivElement | null>(null);

    const messages = selectMessages();
    const hasNewMessage = selectHasNewMessage();

    const { onScroll } = useStickyBottomScroll(wrapperRef, {
        keepPinnedOn: hasNewMessage, // проскроллить только если пользователь не пролистал назад
        forcePinOn: hasNewSentMessage, // проскроллить вниз на каждое новое отправленное сообщение
        onAwayChange: setIsScrolled, // callback на сохранение состояния скролла в стор
    });

    const avatars = useMemo(() => {
        const result = new Map<string, string>();
        const uniqueIds = new Set(messages.map((msg) => msg.senderId));
        uniqueIds.forEach((id) => {
            const participant = selectRemoteParticipantById(id) as Participant;
            const avatar = participant ? getAvatarFromMetadata(participant.metadata) : '';
            result.set(id, typeof avatar === 'string' ? avatar : '');
        });
        return result;
    }, [messages]);

    const messagesToRender = useMemo(
        () =>
            messages.map((msg) => ({
                key: msg.timeMs,
                props: getMessageProps(msg, avatars),
            })),
        [messages, avatars],
    );

    useEffect(() => {
        if (hasNewSentMessage) onScrollAcknowledged();
    }, [hasNewSentMessage, onScrollAcknowledged]);

    if (messages.length === 0) {
        return (
            <NoMessagesWrapper>
                <Typography variant="body3" colorVariant="second">
                    {t('create_chat_no_messages_here_yet')}
                </Typography>
            </NoMessagesWrapper>
        );
    }

    return (
        <MessagesWrapper ref={wrapperRef} onScroll={onScroll}>
            {messagesToRender.map(({ key, props }) => (
                <Message key={key} {...props} />
            ))}
        </MessagesWrapper>
    );
});

MessagesList.displayName = 'MessagesList';
